import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, doc, runTransaction, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { Package, Truck, Home } from 'lucide-react';
import { calculateSellerAmount } from '../utils/commissionUtils';
import DistrictSelect from '../components/shipping/DistrictSelect';
import type { ShippingAddress } from '../types';

interface CartItem {
  productId: string;
  name: string;
  price: number;
  quantity: number;
  mainImage?: string;
  companyId: string;
  companyName: string;
}

interface SellerItems {
  companyName: string;
  items: CartItem[];
  subtotal: number;
}

export default function PrivateSalesCheckout() {
  const [shippingMethod, setShippingMethod] = useState<'normal' | 'dropshipping'>('normal');
  const [companyAddress, setCompanyAddress] = useState('');
  const [companyDistrict, setCompanyDistrict] = useState('');
  const [shippingAddress, setShippingAddress] = useState<ShippingAddress>({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'Portugal',
    district: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [totals, setTotals] = useState({
    subtotal: 0,
    shipping: 0,
    total: 0
  });

  const { currentUser } = useAuth();
  const { items, clearCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser?.companyId) {
      navigate('/login');
      return;
    }

    if (!items.length) {
      navigate('/dashboard/drop');
      return;
    }

    loadCompanyAddress();
    calculateOrderTotals();
  }, [currentUser, items, shippingMethod, companyDistrict, shippingAddress.district]);

  const loadCompanyAddress = async () => {
    if (!currentUser?.companyId) return;

    try {
      const companyDoc = await getDoc(doc(db, 'companies', currentUser.companyId));
      if (companyDoc.exists()) {
        const data = companyDoc.data();
        setCompanyAddress(data.address || '');
        setCompanyDistrict(data.district || '');
      }
    } catch (err) {
      console.error('Error loading company address:', err);
      setError('Erro ao carregar endereço da empresa');
    }
  };

  const groupItemsBySeller = (): Record<string, SellerItems> => {
    return items.reduce((acc, item) => {
      if (!acc[item.companyId]) {
        acc[item.companyId] = {
          companyName: item.companyName,
          items: [],
          subtotal: 0
        };
      }
      acc[item.companyId].items.push(item);
      acc[item.companyId].subtotal += item.price * item.quantity;
      return acc;
    }, {} as Record<string, SellerItems>);
  };

  const getShippingCost = async (district: string): Promise<number> => {
    try {
      const settingsDoc = await getDoc(doc(db, 'settings', 'shipping_costs'));
      if (settingsDoc.exists()) {
        const costs = settingsDoc.data().costs || {};
        return costs[district] || 3.99; // Default to 3.99 if no cost is set
      }
      return 3.99;
    } catch (err) {
      console.error('Error loading shipping costs:', err);
      return 3.99;
    }
  };

  const calculateOrderTotals = async () => {
    const itemsBySeller = groupItemsBySeller();
    let subtotal = 0;
    let shippingTotal = 0;

    try {
      for (const seller of Object.values(itemsBySeller)) {
        subtotal += seller.subtotal;
        const shippingCost = await getShippingCost(
          shippingMethod === 'normal' ? companyDistrict : shippingAddress.district
        );
        shippingTotal += shippingCost;
      }

      setTotals({
        subtotal,
        shipping: shippingTotal,
        total: subtotal + shippingTotal
      });
    } catch (err) {
      console.error('Error calculating totals:', err);
      setError('Erro ao calcular totais');
    }
  };

  const validateForm = (): boolean => {
    if (shippingMethod === 'dropshipping') {
      if (!shippingAddress.name || !shippingAddress.email || !shippingAddress.phone ||
          !shippingAddress.address || !shippingAddress.city || !shippingAddress.postalCode ||
          !shippingAddress.district) {
        setError('Por favor, preencha todos os campos de endereço de entrega');
        return false;
      }
    } else {
      if (!companyAddress || !companyDistrict) {
        setError('Endereço da empresa não configurado');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser?.companyId || !validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const itemsBySeller = groupItemsBySeller();
      const timestamp = new Date();

      await runTransaction(db, async (transaction) => {
        // First perform all reads
        const buyerWalletRef = doc(db, 'wallets', currentUser.companyId);
        const buyerWalletDoc = await transaction.get(buyerWalletRef);

        if (!buyerWalletDoc.exists()) {
          throw new Error('Carteira do comprador não encontrada');
        }

        const buyerBalance = buyerWalletDoc.data().balance;

        if (buyerBalance < totals.total) {
          throw new Error(`Saldo insuficiente. Necessário: €${totals.total.toFixed(2)}`);
        }

        // Process each seller's order
        for (const [sellerId, sellerData] of Object.entries(itemsBySeller)) {
          const sellerWalletRef = doc(db, 'wallets', sellerId);
          const sellerWalletDoc = await transaction.get(sellerWalletRef);

          if (!sellerWalletDoc.exists()) {
            throw new Error(`Carteira do vendedor ${sellerData.companyName} não encontrada`);
          }

          const sellerBalance = sellerWalletDoc.data().balance;
          const shippingCost = await getShippingCost(
            shippingMethod === 'normal' ? companyDistrict : shippingAddress.district
          );

          // Calculate amounts - seller only receives product amount without commission
          const { sellerAmount, commission } = await calculateSellerAmount(
            sellerData.subtotal,
            sellerId,
            false
          );
          const orderTotal = sellerData.subtotal + shippingCost;
          const orderId = `PS${Date.now()}${Math.floor(Math.random() * 1000)}`;

          // Update buyer's wallet
          transaction.update(buyerWalletRef, {
            balance: buyerBalance - orderTotal,
            updatedAt: timestamp
          });

          // Update seller's wallet - only product amount without commission
          transaction.update(sellerWalletRef, {
            balance: sellerBalance + sellerAmount,
            updatedAt: timestamp
          });

          // Create order
          const orderRef = doc(collection(db, 'orders'));
          const orderData = {
            orderNumber: orderId,
            buyerId: currentUser.uid,
            buyerCompanyId: currentUser.companyId,
            sellerId,
            items: sellerData.items.map(item => ({
              productId: item.productId,
              name: item.name,
              quantity: item.quantity,
              price: item.price,
              total: item.price * item.quantity,
              mainImage: item.mainImage
            })),
            shippingMethod,
            shippingAddress: shippingMethod === 'dropshipping' ? shippingAddress : {
              name: currentUser.displayName || '',
              email: currentUser.email || '',
              phone: currentUser.phone || '',
              address: companyAddress,
              city: '',
              postalCode: '',
              country: 'Portugal',
              district: companyDistrict
            },
            subtotal: sellerData.subtotal,
            shippingCost,
            total: orderTotal,
            commission: commission.totalCommission,
            isPrivateSale: true,
            status: 'pending',
            payment: {
              method: 'wallet',
              status: 'completed',
              walletAmount: orderTotal
            },
            createdAt: new Date(),
            createdBy: currentUser.uid,
            comments: [{
              id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              text: `Portes pagos pelo comprador: €${shippingCost.toFixed(2)}`,
              createdAt: new Date(),
              createdBy: 'Sistema'
            }]
          };

          console.log('Criando pedido:', { orderRef: orderRef.path, orderData });
          transaction.set(orderRef, orderData);
          console.log('Pedido criado com sucesso');

          // Only create commission transaction if commission is being charged
          if (commission.totalCommission > 0) {
            const commissionTransactionRef = doc(collection(db, 'walletTransactions'));
            transaction.set(commissionTransactionRef, {
              type: 'commission',
              amount: commission.totalCommission,
              baseAmount: commission.baseCommission,
              vatAmount: commission.vatAmount,
              status: 'completed',
              description: 'Comissão LOGNOW - Venda Privada',
              companyId: sellerId,
              orderId,
              createdAt: timestamp
            });
          }

          // Create payment transaction for buyer
          const buyerTransactionRef = doc(collection(db, 'walletTransactions'));
          transaction.set(buyerTransactionRef, {
            type: 'payment',
            amount: -orderTotal,
            status: 'completed',
            description: `Pagamento de venda privada - ${orderId}`,
            companyId: currentUser.companyId,
            orderId,
            createdAt: timestamp
          });

          // Create payment transaction for seller - only product amount
          const sellerTransactionRef = doc(collection(db, 'walletTransactions'));
          transaction.set(sellerTransactionRef, {
            type: 'payment',
            amount: sellerAmount,
            status: 'completed',
            description: `Recebimento de venda privada - ${orderId}`,
            companyId: sellerId,
            orderId,
            createdAt: timestamp
          });
        }
      });

      clearCart();
      navigate('/dashboard/drop/orders');
    } catch (err) {
      console.error('Error creating order:', err);
      setError(err instanceof Error ? err.message : 'Erro ao criar encomenda');
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-md p-8">
        <h2 className="text-2xl font-bold text-secondary mb-6">Finalizar Compra</h2>

        {error && (
          <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Shipping Method Selection */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Método de Envio</h3>
            <div className="space-y-4">
              <label className="flex items-center gap-3 p-4 border rounded-lg cursor-pointer hover:border-primary">
                <input
                  type="radio"
                  name="shippingMethod"
                  value="normal"
                  checked={shippingMethod === 'normal'}
                  onChange={(e) => setShippingMethod(e.target.value as 'normal' | 'dropshipping')}
                  className="text-primary focus:ring-primary"
                />
                <div>
                  <div className="flex items-center gap-2">
                    <Truck size={20} className="text-gray-400" />
                    <span className="font-medium">Envio Normal</span>
                  </div>
                  <p className="text-sm text-gray-500">
                    Envio para o endereço da sua empresa
                  </p>
                </div>
              </label>

              <label className="flex items-center gap-3 p-4 border rounded-lg cursor-pointer hover:border-primary">
                <input
                  type="radio"
                  name="shippingMethod"
                  value="dropshipping"
                  checked={shippingMethod === 'dropshipping'}
                  onChange={(e) => setShippingMethod(e.target.value as 'normal' | 'dropshipping')}
                  className="text-primary focus:ring-primary"
                />
                <div>
                  <div className="flex items-center gap-2">
                    <Home size={20} className="text-gray-400" />
                    <span className="font-medium">Envio para Cliente</span>
                  </div>
                  <p className="text-sm text-gray-500">
                    Envio direto para o endereço do seu cliente
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Shipping Address */}
          {shippingMethod === 'dropshipping' ? (
            <div>
              <h3 className="text-lg font-semibold mb-4">Endereço de Entrega</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nome</label>
                  <input
                    type="text"
                    required
                    value={shippingAddress.name}
                    onChange={(e) => setShippingAddress({ ...shippingAddress, name: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    required
                    value={shippingAddress.email}
                    onChange={(e) => setShippingAddress({ ...shippingAddress, email: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Telefone</label>
                  <input
                    type="tel"
                    required
                    value={shippingAddress.phone}
                    onChange={(e) => setShippingAddress({ ...shippingAddress, phone: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Morada</label>
                  <input
                    type="text"
                    required
                    value={shippingAddress.address}
                    onChange={(e) => setShippingAddress({ ...shippingAddress, address: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Cidade</label>
                  <input
                    type="text"
                    required
                    value={shippingAddress.city}
                    onChange={(e) => setShippingAddress({ ...shippingAddress, city: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Código Postal</label>
                  <input
                    type="text"
                    required
                    value={shippingAddress.postalCode}
                    onChange={(e) => setShippingAddress({ ...shippingAddress, postalCode: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Distrito</label>
                  <DistrictSelect
                    value={shippingAddress.district}
                    onChange={(district) => setShippingAddress({ ...shippingAddress, district })}
                    required
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div>
              <h3 className="text-lg font-semibold mb-4">Endereço da Empresa</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p>{companyAddress}</p>
                <p>Distrito: {companyDistrict}</p>
              </div>
            </div>
          )}

          {/* Order Summary */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Resumo do Pedido</h3>
            <div className="space-y-4">
              {Object.entries(groupItemsBySeller()).map(([sellerId, sellerData]) => (
                <div key={sellerId} className="border rounded-lg p-4">
                  <h4 className="font-medium mb-2">Vendedor: {sellerData.companyName}</h4>
                  <div className="space-y-2">
                    {sellerData.items.map((item) => (
                      <div key={item.productId} className="flex items-center gap-4">
                        {item.mainImage ? (
                          <img
                            src={item.mainImage}
                            alt={item.name}
                            className="w-16 h-16 object-cover rounded"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center">
                            <Package className="w-8 h-8 text-gray-400" />
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="font-medium">{item.name}</p>
                          <p className="text-sm text-gray-600">
                            {item.quantity} x €{item.price.toFixed(2)}
                          </p>
                        </div>
                        <p className="font-medium">
                          €{(item.price * item.quantity).toFixed(2)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">Subtotal</span>
                  <span>€{totals.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">Envio</span>
                  <span>€{totals.shipping.toFixed(2)}</span>
                </div>
                <div className="flex justify-between pt-2 border-t">
                  <span className="font-medium">Total</span>
                  <span className="font-medium">€{totals.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/dashboard/drop')}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Voltar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-primary text-secondary rounded-md hover:bg-primary-dark disabled:opacity-50"
            >
              {loading ? 'Processando...' : 'Finalizar Compra'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}